import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../../Auth/AuthContext';
import { Typography, Paper, Box, Button, IconButton, InputLabel } from '@mui/material';
import { DialogTitle, DialogContent, DialogActions, TextField, InputAdornment } from '@mui/material';
import Dialog from '@mui/material/Dialog';

import TablaMovimientosEmpleado from './TablaMovimientosEmpleado';
import DialogFinalizarTurno from './DialogFinalizarTurno';

import { Edit } from '@mui/icons-material';
import { ArrowBack } from '@mui/icons-material';

import { useTheme } from '@emotion/react';
import { formatApiMoneyForInput, parseColombianMoneyInput } from '../../../utils/money';

const formatNumber = (num) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

const API_URL = process.env.REACT_APP_API_URL;

async function actualizarReporteDiario(v, fecha) {
  const valor = v === undefined ? 0 : v
  try {
      const response = await axios.get(`${API_URL}/reporte_diario_cajas/`);
      const reportes = await response.data, reporteHoy = reportes.find(r => r.fecha === fecha && r.tipo === 'dinero_base');

      if (reporteHoy) {
        const dif = Math.abs(Number(reporteHoy.valor) - Number(valor));
        const valorAPagar = Number(valor) >= Number(reporteHoy.valor) ? Number(reporteHoy.valor) + dif : Number(reporteHoy.valor) - dif;

          await axios.put(`${API_URL}/reporte_diario_cajas/${reporteHoy.id}/`, { valor: Number(valorAPagar) } );
      }
  } catch (error) {
      console.error("Error en la actualización del reporte:", error);
  }
}

function formatearFecha(fecha) {
  // Si ya parece estar formateada, la devolvemos como está
  if (typeof fecha === 'string' && fecha.includes('de') && fecha.includes(':')) {
    return fecha;
  }

  const date = new Date(fecha);
  return date.toLocaleString('es-CO', {
    year: "numeric",
    month: "long",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

function VistaTurno({goBackWithAdmin, currentCaja, handleRefresh}) {
  const { user } = useAuth();
  const theme = useTheme();

  const [montoBase, setMontoBase] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const handleEditClick = () => {setEditDialogOpen(true);};
  const handleEditDialogClose = () => {setEditDialogOpen(false);};  

  useEffect(() => {
    if (!currentCaja) return;
    setMontoBase(formatApiMoneyForInput(currentCaja.dinero_base));
  }, [currentCaja]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogFinalizarOpen, setDialogFinalizarOpen] = useState(false);

  const handleDialogFinalizarOpen = () => {setDialogFinalizarOpen(true);}
  const handleDialogFinalizarClose = () => {setDialogFinalizarOpen(false);}

  const handleEditarMontoBase = async () => {
    try {
      const canonicalAmount = parseColombianMoneyInput(montoBase);
      const cajaModificada = await axios.put(`${API_URL}/cajas/${currentCaja.id}/`, {
        dinero_base: canonicalAmount,
        nombre: currentCaja.nombre,
        movimientos: currentCaja.movimientos.map(movimiento => movimiento.id)
      });

      await actualizarReporteDiario(cajaModificada.data.dinero_base, cajaModificada.data.fecha_hora_inicio.split(" ")[0]);
      // Lógica adicional según sea necesario (actualización de estado, etc.)
      handleEditDialogClose(); // Cierra el diálogo después de guardar
      handleRefresh(); // Actualiza la lista de cajas u otra acción necesaria
    } catch (error) {
      console.error('Error al actualizar el monto base:', error);
      // Manejo de errores
    }
  };

  return (
    <>
      {currentCaja ? (
        <>
          {user.rol === 'Administrador' && <IconButton 
            color="primary" 
            sx={{
              zIndex: 1, // Asegura que esté por encima de otros elementos
            }}
            onClick={goBackWithAdmin}
          >
            <ArrowBack />
          </IconButton>}

          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ paddingTop: '20px' }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }} gutterBottom>
              {currentCaja.nombre}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", padding: 2 }}>
              <>
                {user.rol === 'Administrador' && <IconButton
                  color="primary"
                  sx={{
                    zIndex: 1,
                    marginRight: '10px',
                  }}
                  onClick={handleEditClick} // Define esta función
                >
                  <Edit />
                </IconButton>}
              </>
            </Box>
          </Box>
          <Paper elevation={3} sx={{ padding: '20px' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', paddingBottom: 0 }} gutterBottom>
                Total Sin Monto: {formatNumber((Number(currentCaja.total_ingresos) - Number(currentCaja.total_egresos)).toFixed(2))}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', paddingBottom: 0 }} gutterBottom>
                Total Con Monto: {formatNumber(((Number(currentCaja.total_ingresos) - Number(currentCaja.total_egresos)) + Number(currentCaja.dinero_base)).toFixed(2))}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', paddingBottom: 0 }} gutterBottom>
                Total Ingresos: {formatNumber(currentCaja.total_ingresos)}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', paddingTop: 0 }} gutterBottom>
                Total Egresos: {formatNumber(currentCaja.total_egresos)}
              </Typography>
            </Box>
            <Typography variant="body1" gutterBottom>
              <strong>Empleado:</strong> {currentCaja.nombre.split('-')[0]}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <strong>Monto Base:</strong> {formatNumber(currentCaja.dinero_base)}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <strong>Fecha y Hora de Creación:</strong> {formatearFecha(currentCaja.fecha_hora_inicio)}
            </Typography>
          </Paper>

          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ paddingTop: '20px' }}>
            <Typography variant="h4" gutterBottom sx={{fontWeight: 'bold'}}>
              Movimientos: {currentCaja.movimientos.length}
            </Typography>
          </Box>
          <TablaMovimientosEmpleado cajaId={currentCaja.id} handleRefresh={handleRefresh}/>

          {user.rol !== 'Administrador' && <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", paddingTop: 2 }}>
            <Button
              variant="outlined"
              color="error"
              onClick={handleDialogFinalizarOpen}
              sx={{ minWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word', padding: '8px 16px' }}
            >
              Finalizar Turno
            </Button>
          </Box>}

          <DialogFinalizarTurno
            dialogOpen={dialogFinalizarOpen}
            handleDialogClose={handleDialogFinalizarClose}
            currentCaja={currentCaja}
          />

          <Dialog open={editDialogOpen} onClose={handleEditDialogClose}>
            <DialogTitle>Edita las Características Editables de esta Caja:</DialogTitle>
            <DialogContent>
              <InputLabel>Monto Base</InputLabel>
              <TextField
                type="text"
                inputMode="decimal"
                value={montoBase}
                onChange={(e) => setMontoBase(e.target.value)}
                fullWidth
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, maxLength: 10 }} 
              />
            </DialogContent>
            <DialogActions>
              <Button variant='outlined'onClick={handleEditDialogClose} color="error">
                Cancelar
              </Button>
              <Button variant='contained'onClick={handleEditarMontoBase} color="primary">
                Guardar
              </Button>
            </DialogActions>
          </Dialog>
          
        </>
      ) : <h1>Cargando...</h1>}
    </>
  );
}

export default VistaTurno;
